import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import { addCycleAlignmentTable } from './cycleAlignmentTable';
import { usesMonitor } from '../cycleLogic';
import { getTodayKey } from '../cycleTiming';
import { buildCycleSummaryRows } from './cycleSummaryTable';
import { formatTemperature } from '../temperature';

interface Observation {
  date: string;
  cycle_day: number;
  bleeding?: number;
  monitor?: string;
  intercourse?: boolean;
  mucus?: string;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  notes?: string;
  symptoms?: string[];
  temperature_f?: number;
  ovulation_test?: string | null;
  /** In a partial cycle — cycle_day is an offset from the first logged day, not a real CD. */
  cycle_start_inferred?: boolean;
}

interface Cycle {
  start_date: string;
  end_date?: string | null;
  first_peak_cd?: number | null;
  /** Partial cycle: logged before the first recorded cycle start, so its CD 1 is unknown. */
  start_inferred?: boolean;
}

export const generatePDF = (
  observations: Observation[],
  cycles: Cycle[],
  userName: string,
  nfpMethods: string[] = ["Marquette"],
  /** Owner's timezone, so "today" (current vs past vs upcoming) matches the app. */
  timeZone?: string | null
) => {
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(20);
  doc.text('Fertility Tracking Report', 14, 20);
  
  doc.setFontSize(10);
  doc.text(`Generated for: ${userName}`, 14, 28);
  doc.text(`Date: ${format(new Date(), 'PPP')}`, 14, 34);
  
  // Cycle Summary
  doc.setFontSize(14);
  doc.text('Cycle Summary', 14, 45);
  
  const todayKey = getTodayKey(timeZone);
  const cycleData = buildCycleSummaryRows(cycles, todayKey);
  
  autoTable(doc, {
    startY: 50,
    head: [['Cycle', 'Start Date', 'End Date', 'Length', 'Peak Day']],
    body: cycleData,
    theme: 'grid',
    headStyles: { fillColor: [99, 102, 241] },
  });

  // Cycle alignment view (day 1 aligned across recent cycles)
  doc.addPage();
  doc.setFontSize(14);
  doc.text('Cycle Day Alignment (Recent Cycles)', 14, 20);
  doc.setFontSize(8);
  doc.text('Legend: B=Bleeding, P/H/L=Monitor, M=Mucus, Z=Post-peak mucus (non-fertile), I=Intercourse, T=Temp, S=Symptoms, O=Positive OPK', 14, 24);
  // Day-1 alignment needs a known CD 1, so partial cycles are left out of it.
  addCycleAlignmentTable(doc, observations, cycles.filter((c) => !c.start_inferred), {
    methods: nfpMethods,
    todayKey,
  });
  
  // Observations Table
  doc.addPage();
  doc.setFontSize(14);
  doc.text('Daily Observations', 14, 20);
  
  const showMonitorColumn = usesMonitor(nfpMethods);

  const obsData = observations.slice(0, 90).map(obs => {
    const baseData = [
      format(parseISO(obs.date), 'PP'),
      obs.cycle_start_inferred ? '?' : obs.cycle_day,
      obs.temperature_f ? formatTemperature(obs.temperature_f, '°F') : '-',
      obs.bleeding ? `Level ${obs.bleeding}` : '-',
    ];

    if (showMonitorColumn) {
      baseData.push(obs.monitor || '-');
    }

    baseData.push(
      obs.ovulation_test === 'positive' ? 'Pos' : obs.ovulation_test === 'negative' ? 'Neg' : '-',
      obs.intercourse ? 'Yes' : 'No',
      obs.mucus || '-',
      obs.symptoms ? obs.symptoms.join(', ') : '-',
      obs.notes || '-'
    );

    return baseData;
  });

  const headers = !showMonitorColumn
    ? [['Date', 'CD', 'Temp', 'Bleeding', 'OPK', 'Inter.', 'Mucus', 'Symptoms', 'Notes']]
    : [['Date', 'CD', 'Temp', 'Bleeding', 'Monitor', 'OPK', 'Inter.', 'Mucus', 'Symptoms', 'Notes']];

  const columnStyles = !showMonitorColumn
    ? { 4: { cellWidth: 11 }, 7: { cellWidth: 25 }, 8: { cellWidth: 30 } }
    : { 5: { cellWidth: 11 }, 8: { cellWidth: 25 }, 9: { cellWidth: 30 } };
  
  autoTable(doc, {
    startY: 25,
    head: headers,
    body: obsData,
    theme: 'striped',
    headStyles: { fillColor: [99, 102, 241] },
    styles: { fontSize: 7 },
    columnStyles
  });
  
  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  doc.save(`fertility-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};
